import React, { useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppTheme } from './context';
import { zIndex } from './styles/utilities';

/**
 * Bridges the app ThemeProvider into MUI so CssBaseline, Paper, and default
 * surfaces follow light/dark instead of always using MUI's light palette.
 *
 * Also raises Menu / Popover / Autocomplete above app dialogs (see zIndex.floatingMenu).
 * Dialog Selects should still pass `$dialogMenuProps` for disableScrollLock.
 */
export const ThemedMuiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useAppTheme();
  const { colors, isDark } = theme;

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          background: {
            default: colors.background,
            paper: colors.palette.input.background,
          },
          text: {
            primary: colors.text,
            secondary: colors.textDim,
          },
          primary: {
            main: colors.primary,
          },
          secondary: {
            main: colors.secondary,
          },
          divider: colors.palette.border,
          error: {
            main: colors.error,
          },
        },
        components: {
          MuiMenu: {
            defaultProps: {
              style: { zIndex: zIndex.floatingMenu },
            },
          },
          MuiPopover: {
            defaultProps: {
              style: { zIndex: zIndex.floatingMenu },
            },
          },
          MuiAutocomplete: {
            defaultProps: {
              componentsProps: {
                popper: {
                  style: { zIndex: zIndex.floatingMenu },
                },
              },
            },
          },
        },
      }),
    [colors, isDark],
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      {children}
    </MuiThemeProvider>
  );
};
